import { useCallback, useRef, useState } from 'react';

/**
 * Wraps an async action with re-entrancy protection: while a call is in flight,
 * further invocations are dropped. The `inFlight` ref is checked synchronously,
 * so it blocks rapid clicks that fire within the same event-loop tick (before
 * the `pending` state has a chance to re-render and disable the control).
 *
 * Shared by both the marketing site and the web app — keep it dependency-free.
 *
 * Usage:
 *   const { run, pending } = useAsyncAction(async () => { await api.submit(); });
 *   <button onClick={run} disabled={pending}>{pending ? 'Sending…' : 'Submit'}</button>
 */
export function useAsyncAction<A extends unknown[]>(
  fn: (...args: A) => Promise<void> | void,
) {
  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);

  const run = useCallback(async (...args: A) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    try {
      await fn(...args);
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }, [fn]);

  return { run, pending };
}
